import React, { useState } from "react";
import { CheckOutlined } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentCopyOutlined from "../IconComponents/ContentCopyOutlined";

interface CopyToClipboardProps {
	text: string;
	showSnackbar?: boolean;
	snackbarMessage?: string;
	tooltipText?: string;
	copiedTooltipText?: string;
	iconClassName?: string;
	successDuration?: number;
	onClick?: () => void;
	iconSize?: "small" | "inherit" | "medium" | "large";
}

const useStyles = makeStyles((theme) => ({
	copyIcon: {
		fontSize: theme.typography.fontSize,
		marginLeft: theme.spacing(0.5),
		verticalAlign: "text-bottom",
		cursor: "pointer",
		color: theme.palette.custom.jbColours.brightBlue,
		transition: "transform 0.2s ease, color 0.2s ease",
		"&:hover": {
			transform: "scale(1.15)",
		},
	},
	successIcon: {
		color: theme.palette.success.main,
		transform: "scale(1.3)",
	},
	iconWrapper: {
		display: "inline-flex",
		alignItems: "center",
	},
}));

export const CopyToClipboard: React.FC<CopyToClipboardProps> = ({
	text,
	tooltipText = "Copy",
	copiedTooltipText = "Copied!",
	iconClassName,
	successDuration = 1500,
	onClick,
	iconSize = "medium",
}) => {
	const classes = useStyles();
	const [copied, setCopied] = useState(false);

	const handleCopy = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
		e.stopPropagation();

		navigator.clipboard
			.writeText(text)
			.then(() => {
				setCopied(true);

				if (onClick) {
					onClick();
				}

				setTimeout(() => {
					setCopied(false);
				}, successDuration);
			})
			.catch((err) => {
				console.error("Failed to copy text: ", err);
			});
	};

	return (
		<>
			{!copied ? (
				<Tooltip title={tooltipText} placement="top">
					<span className={classes.iconWrapper}>
						<ContentCopyOutlined
							className={`${classes.copyIcon} ${iconClassName || ""}`}
							onClick={handleCopy}
							aria-label={tooltipText}
							fontSize={iconSize}
						/>
					</span>
				</Tooltip>
			) : (
				<Tooltip title={copiedTooltipText} placement="top">
					<span className={classes.iconWrapper}>
						<CheckOutlined className={`${classes.copyIcon} ${classes.successIcon} ${iconClassName || ""}`} />
					</span>
				</Tooltip>
			)}
		</>
	);
};

export default CopyToClipboard;
